/**
 * Cookie-consent domain model + persistence helpers.
 *
 * Pure module: no React, no side effects at import time. All browser access
 * happens inside functions that are only called from client code, and every
 * risky call is guarded so a blocked-storage environment (iOS "Block All
 * Cookies") can never throw into React.
 */

import { safeStorage } from './safeStorage';

export type ConsentCategory = 'necessary' | 'analytics' | 'marketing';

export type Categories = Record<ConsentCategory, boolean>;

export interface ConsentState {
    version: number;
    categories: Categories;
    /** ISO timestamp of when the decision was recorded (audit trail). */
    decidedAt: string;
}

/** Bump when the cookie policy / category set changes to force a re-prompt. */
export const CONSENT_VERSION = 1;

/** Primary persistence key (JSON payload). */
export const CONSENT_KEY = 'fintek:cookie-consent';

/** Fallback cookie name when localStorage is unavailable. */
const CONSENT_COOKIE = 'fintek_cc';

export const ACCEPT_ALL: Categories = {
    necessary: true,
    analytics: true,
    marketing: true,
};

export const NECESSARY_ONLY: Categories = {
    necessary: true,
    analytics: false,
    marketing: false,
};

function isCategories(value: unknown): value is Categories {
    if (!value || typeof value !== 'object') return false;
    const c = value as Record<string, unknown>;
    return (
        typeof c.necessary === 'boolean' &&
        typeof c.analytics === 'boolean' &&
        typeof c.marketing === 'boolean'
    );
}

/**
 * Parse a persisted consent payload. Returns null on any structural mismatch
 * so callers can treat corruption as "no decision made".
 */
export function parseConsent(raw: string | null): ConsentState | null {
    if (!raw) return null;
    try {
        const data = JSON.parse(raw) as Partial<ConsentState>;
        if (
            data &&
            typeof data.version === 'number' &&
            isCategories(data.categories)
        ) {
            return {
                version: data.version,
                categories: { ...data.categories, necessary: true },
                decidedAt: typeof data.decidedAt === 'string' ? data.decidedAt : '',
            };
        }
    } catch {
        /* fall through to null */
    }
    return null;
}

/**
 * Persist consent, preferring localStorage. If localStorage is blocked, fall
 * back to a session cookie. Returns true when the decision was durably stored.
 */
export function persistConsent(state: ConsentState): boolean {
    if (safeStorage.set(CONSENT_KEY, JSON.stringify(state))) return true;
    return writeCookieFallback(state);
}

/** Best-effort read from localStorage then the fallback cookie. */
export function loadConsent(): ConsentState | null {
    const fromStore = parseConsent(safeStorage.get(CONSENT_KEY));
    if (fromStore) return fromStore;
    return parseConsent(readCookieFallback());
}

/** Encode a compact `1.1.0` (necessary.analytics.marketing) cookie value. */
export function writeCookieFallback(state: ConsentState): boolean {
    try {
        const c = state.categories;
        const bits = `${c.necessary ? 1 : 0}.${c.analytics ? 1 : 0}.${c.marketing ? 1 : 0}.v${state.version}`;
        // A 13-month expiry mirrors a durable consent decision; when cookies are
        // fully blocked this assignment is silently ignored by the browser.
        document.cookie = `${CONSENT_COOKIE}=${bits}; path=/; max-age=33696000; SameSite=Lax`;
        return true;
    } catch {
        return false;
    }
}

/** Decode the fallback cookie back into a raw JSON consent string. */
export function readCookieFallback(): string | null {
    try {
        const match = document.cookie
            .split('; ')
            .find((row) => row.startsWith(`${CONSENT_COOKIE}=`));
        if (!match) return null;
        const value = decodeURIComponent(match.split('=')[1] || '');
        const [necessary, analytics, marketing, versionPart] = value.split('.');
        const version = Number((versionPart || 'v0').replace(/^v/, ''));
        if (!Number.isFinite(version)) return null;
        const state: ConsentState = {
            version,
            categories: {
                necessary: true,
                analytics: analytics === '1',
                marketing: marketing === '1',
            },
            decidedAt: '',
        };
        return JSON.stringify(state);
    } catch {
        return null;
    }
}

/** Clear any stored decision (used when the user withdraws consent). */
export function clearConsent(): void {
    safeStorage.remove(CONSENT_KEY);
    try {
        document.cookie = `${CONSENT_COOKIE}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
    } catch {
        /* ignore */
    }
}
